import { useState, type FC, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HotkeysProvider } from '@tanstack/react-hotkeys';
import { TooltipProvider } from '@ziggiz/ui';
import { ThemeProvider } from 'next-themes';
import { HelmetProvider } from 'react-helmet-async';
import { createQueryClient } from '@/lib/query-client';
import { APP_HOTKEYS } from '@/navigation/app-hotkeys';

interface AppProvidersProps {
  children: ReactNode;
  showDevtools?: boolean;
}

/**
 * Everything that must sit above the router. Providers that need router
 * context (e.g. `QueryParamProvider`) live in `RootLayout` instead.
 */
export const AppProviders: FC<AppProvidersProps> = ({
  children,
  showDevtools = import.meta.env.DEV
}) => {
  const [queryClient] = useState(createQueryClient);

  return (
    <HelmetProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <QueryClientProvider client={queryClient}>
          {/* Every app hotkey binds through this; pages add scoped providers. */}
          <HotkeysProvider appHotkeys={APP_HOTKEYS}>
            <TooltipProvider>{children}</TooltipProvider>
          </HotkeysProvider>
          {showDevtools ? <ReactQueryDevtools buttonPosition="bottom-left" /> : null}
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};
